import React, { useState, memo, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Star, MapPin, Check, Info, X, ExternalLink, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import useEmblaCarousel from 'embla-carousel-react';
import RoomRatesModal from '@/components/stays/RoomRatesModal';
import { fetchRoomRates } from '@/api/functions';
import { SLICE_PAY_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { resolveHotelRating } from '@/utils/hotelRating';

const formatPrice = (price) => {
  if (typeof price === 'number') {
    const isWholeNumber = price % 1 === 0;
    const formatted = isWholeNumber ? price.toFixed(0) : price.toFixed(2);
    return `$${formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
  return price;
};

function StayOfferCard({ hotel, onHotelSelect, isSelected = false, hidePrice = false, flightData = null }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPropertyDetails, setShowPropertyDetails] = useState(false);
  const [showRoomRatesModal, setShowRoomRatesModal] = useState(false);
  const [hotelWithRates, setHotelWithRates] = useState(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const isSelectable = !!onHotelSelect;
  
  // Calculate combined flight + hotel pricing with upfront payment
  const combinedPricing = useMemo(() => {
    if (!flightData || !hotel?.total_amount) {
      return null;
    }
    
    try {
      const { selectedFlight, returnFlight, bookingOption } = flightData;
      
      // CRITICAL: Use the chosen fare's price from bookingOption when available.
      // The user sees "$150 due today" on FlightOptions from mainOption.price - we must use that.
      // selectedFlight/returnFlight.total_amount can differ (e.g. raw offer vs chosen fare class).
      let flightBasePrice;
      const mainOption = bookingOption?.together || bookingOption?.departing;
      const chosenFarePrice = mainOption ? parseFloat(mainOption.price) : NaN;
      
      if (!isNaN(chosenFarePrice) && chosenFarePrice > 0) {
        flightBasePrice = chosenFarePrice;
      } else if (returnFlight) {
        flightBasePrice = parseFloat(selectedFlight.total_amount || selectedFlight.price || 0) + parseFloat(returnFlight.total_amount || returnFlight.price || 0);
      } else if (selectedFlight.slices?.length >= 2) {
        flightBasePrice = parseFloat(selectedFlight.total_amount || selectedFlight.base_amount || selectedFlight.price || 0);
      } else {
        flightBasePrice = parseFloat(selectedFlight.total_amount || selectedFlight.base_amount || selectedFlight.price || 0);
      }
      
      // Prefer the specifically selected room rate when the customer has already
      // picked a room in RoomRatesModal. On the search list `selectedRate` is
      // undefined and `hotel.total_amount` is the property's cheapest rate — so
      // we flag that case as an estimate ("from") to make it clear the actual
      // deposit will move with the room they choose. Search results write
      // `hotel.total_amount = result.cheapest_rate_total_amount` (see
      // `server/duffel-stays.js`), which is why the list card shows the same
      // "today" figure for every room type until one is selected.
      const hasSelectedRate = hotel.selectedRate?.total_amount != null;
      const hotelBasePrice = parseFloat(
        hasSelectedRate ? hotel.selectedRate.total_amount : (hotel.total_amount || 0)
      );
      
      // CRITICAL: Combine flight + hotel FIRST, then calculate fees ONCE
      // Service fee WAIVED for flight+hotel packages - base only, no service fee
      const combinedBasePrice = flightBasePrice + hotelBasePrice;
      const serviceFee = 0; // Waived for flight+hotel
      const subtotalBeforeHold = combinedBasePrice + serviceFee;
      
      // Deposit = % of (base fare + service fee), not total with hold
      const defaultDepositPct = 35;
      const upfrontAmount = Math.round((subtotalBeforeHold * (defaultDepositPct / 100)) * 100) / 100;
      
      // What checkout will actually bill for the package: service fee is
      // waived on flight+hotel, but the 15% hold fee still applies to the
      // combined subtotal (Review's grandTotalIncludingSeats math). The card
      // previously labeled the hotel component alone as "total".
      const packageAllInTotal = Math.ceil(subtotalBeforeHold * (1 + SLICE_PAY_HOLD_FEE_RATE));

      return {
        upfrontAmount,
        hotelBasePrice,
        packageAllInTotal,
        isPayInFull: false,
        isEstimate: !hasSelectedRate,
      };
    } catch (error) {
      console.error('Error calculating combined pricing:', error);
      return null;
    }
  }, [flightData, hotel]);
  
  // Carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const handleSelect = async () => {
    if (!isSelectable || !onHotelSelect) return;

    try {
      setIsFetchingRates(true);
      console.log('Fetching room rates for hotel:', hotel.search_result_id);
      
      const { data: hotelData } = await fetchRoomRates(hotel.search_result_id);
      
      setHotelWithRates(hotelData);
      setShowRoomRatesModal(true);
    } catch (error) {
      console.error('Failed to fetch room rates:', error);
      alert('Failed to load room options. Please try again.');
    } finally {
      setIsFetchingRates(false);
    }
  };

  const handleRateSelection = (selectedRate) => {
    if (isSelectable && onHotelSelect) {
      onHotelSelect(hotel, selectedRate);
    }
  };

  // Upgrade image quality by modifying Google image URL parameters
  const upgradeImageQuality = (url) => {
    if (!url) return null;
    
    try {
      // Google image URLs have size parameters like w=226-h=150
      // We'll increase them for better quality
      let upgradedUrl = url;
      
      // Replace small width/height params with larger ones
      upgradedUrl = upgradedUrl.replace(/w=\d+/g, 'w=800');
      upgradedUrl = upgradedUrl.replace(/h=\d+/g, 'h=600');
      
      // Remove -k-no which applies compression
      upgradedUrl = upgradedUrl.replace(/-k-no/g, '');
      
      return upgradedUrl;
    } catch (error) {
      return url;
    }
  };

  // Get hotel image URL (handle both string and object formats)
  const getHotelImageUrl = () => {
    // First check if there's a direct thumbnail property on hotel
    if (hotel.thumbnail) {
      return upgradeImageQuality(hotel.thumbnail);
    }
    
    // Then check images array
    if (hotel.images && hotel.images.length > 0) {
      const firstImage = hotel.images[0];
      // Check if it's an object with thumbnail/original_image properties
      if (typeof firstImage === 'object' && firstImage !== null) {
        return upgradeImageQuality(firstImage.thumbnail || firstImage.original_image);
      }
      // Otherwise, it's a string URL
      return upgradeImageQuality(firstImage);
    }
    
    return null;
  };

  const hotelImageUrl = getHotelImageUrl();
  // Duffel scores out of 10 and Google out of 5: label on the source's scale,
  // stars on five.
  const rating = resolveHotelRating(hotel);

  if (!hotel) return null;

  return (
    <>
      <div
        onClick={handleSelect}
        className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
          isSelected
            ? 'ring-2 ring-white bg-white/10'
            : 'bg-white/5 hover:bg-white/10'
        } border border-white/10 cursor-pointer ${isFetchingRates ? 'opacity-50' : ''}`}
      >
        <div className="p-3 md:p-4">
          <div className="flex flex-col md:flex-row md:items-start gap-3">
            {/* Hotel Image */}
            {hotelImageUrl && (
              <div className="w-full md:w-48 h-52 md:h-32 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={hotelImageUrl}
                  alt={hotel.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23374151" width="200" height="200"/%3E%3C/svg%3E';
                  }}
                />
              </div>
            )}

          {/* Hotel Details */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-2 mb-0">
              {/* Hotel Title Row */}
              <h3 className="text-base font-medium text-white line-clamp-1">
                {hotel.name}
              </h3>
              
              {/* Rating, Amenities, and Price Row (Mobile) */}
              <div className="flex items-start justify-between gap-2 mb-1.5 md:mb-0">
                <div className="flex-1 min-w-0">
                  {/* Rating and Stars */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {rating && (
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-2.5 h-2.5 ${
                              i < Math.floor(rating.outOfFive)
                                ? 'fill-amber-400 text-amber-400'
                                : i < rating.outOfFive
                                ? 'fill-amber-400/50 text-amber-400'
                                : 'fill-none text-white/20'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    {rating && (
                      <div className="px-1.5 py-0.5 rounded-full text-[9px] bg-white/[0.03] text-white/50 border border-white/[0.08] whitespace-nowrap">
                        {rating.label} ({hotel.reviews || 0})
                      </div>
                    )}
                  </div>

                  {/* Amenities */}
                  {hotel.amenities && hotel.amenities.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {hotel.amenities.slice(0, 3).map((amenity, index) => (
                        <span
                          key={index}
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.03] text-white/40 border border-white/[0.08]"
                        >
                          {amenity}
                        </span>
                      ))}
                      {hotel.amenities.length > 3 && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.03] text-white/40 border border-white/[0.08]"
                        >
                          +{hotel.amenities.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Price and Nights (Mobile Only) */}
                <div className="text-right flex-shrink-0 md:hidden">
                  {!hidePrice && hotel.total_amount && (
                    <div className="mb-1">
                      {combinedPricing ? (
                        <>
                          <div className="text-lg font-semibold text-white whitespace-nowrap">
                            {combinedPricing.isEstimate ? 'from ' : ''}{formatPrice(combinedPricing.upfrontAmount)} today
                          </div>
                          <div className="text-[9px] text-white/60">
                            {combinedPricing.isEstimate ? 'from ' : ''}{formatPrice(combinedPricing.packageAllInTotal)} total
                          </div>
                          {hotel.nightly_rate && (
                            <div className="text-[9px] text-white/60">
                              {formatPrice(hotel.nightly_rate)}/night
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-semibold text-white whitespace-nowrap">
                            {formatPrice(hotel.total_amount)}
                          </div>
                          {hotel.nightly_rate && (
                            <div className="text-[9px] text-white/60">
                              {formatPrice(hotel.nightly_rate)}/night
                            </div>
                          )}
                        </>
                      )}
                      {/* Due at Hotel notice (Mobile) - shows when rate is selected or from metadata */}
                      {(() => {
                        const feeAmount = hotel.selectedRate?.due_at_accommodation_amount || hotel.metadata?.due_at_accommodation_amount;
                        return feeAmount && parseFloat(feeAmount) > 0 ? (
                          <div className="mt-1 text-[9px] text-amber-400">
                            + {formatPrice(parseFloat(feeAmount))} due at hotel
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                  {hotel.nights && (
                    <div className="text-xs text-white/70 whitespace-nowrap">
                      {hotel.nights} {hotel.nights === 1 ? 'night' : 'nights'}
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing Row (Desktop Only - Below title on far right) */}
              <div className="hidden md:flex justify-end">
                <div className="flex flex-col items-end gap-1">
                  {/* Price Display */}
                  {!hidePrice && hotel.total_amount && (
                    <div className="mb-1">
                      {combinedPricing ? (
                        <>
                          <div className="text-base font-medium text-white whitespace-nowrap">
                            {combinedPricing.isEstimate ? 'from ' : ''}{formatPrice(combinedPricing.upfrontAmount)} today
                          </div>
                          <div className="text-xs text-white/60 text-right">
                            {combinedPricing.isEstimate ? 'from ' : ''}{formatPrice(combinedPricing.packageAllInTotal)} total
                          </div>
                          {hotel.nightly_rate && (
                            <div className="text-xs text-white/60 text-right">
                              {formatPrice(hotel.nightly_rate)}/night
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-base font-medium text-white whitespace-nowrap">
                            {formatPrice(hotel.total_amount)}
                          </div>
                          {hotel.nightly_rate && (
                            <div className="text-xs text-white/60 text-right">
                              {formatPrice(hotel.nightly_rate)}/night
                            </div>
                          )}
                        </>
                      )}
                      {/* Due at Hotel notice (Desktop) - shows when rate is selected or from metadata */}
                      {(() => {
                        const feeAmount = hotel.selectedRate?.due_at_accommodation_amount || hotel.metadata?.due_at_accommodation_amount;
                        return feeAmount && parseFloat(feeAmount) > 0 ? (
                          <div className="mt-1 px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-400 text-right">
                            + {formatPrice(parseFloat(feeAmount))} due at hotel
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {hotel.nights && (
                    <div className="text-sm text-white/80">
                      {hotel.nights} {hotel.nights === 1 ? 'night' : 'nights'}
                    </div>
                  )}

                  {isFetchingRates && (
                    <div className="flex items-center gap-1.5 text-white/60 text-xs px-2 py-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  )}
                  {isSelected && !isFetchingRates && (
                    <div className="flex items-center gap-1.5 text-emerald-400 text-xs px-2 py-1">
                      <Check className="w-3 h-3" />
                      <span>Selected</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              {hotel.address && (
                <div className="text-xs text-white/60 line-clamp-1">
                  {hotel.address.full || hotel.address.city || hotel.address.country}
                </div>
              )}
            </div>

            {/* Expand/Collapse Details */}
            {hotel.description && (
              <div className="-mt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                  className="text-white/60 hover:text-white hover:bg-white/5 p-0 h-auto font-normal"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Hide details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Show details
                    </>
                  )}
                </Button>

                {isExpanded && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <p className="text-sm text-white/70 leading-relaxed">
                      {hotel.description}
                    </p>
                    
                    {/* All Amenities */}
                    {hotel.amenities && hotel.amenities.length > 0 && (
                      <div className="mt-3">
                        <h4 className="text-sm font-medium text-white mb-2">Amenities</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {hotel.amenities.map((amenity, index) => (
                            <div
                              key={index}
                              className="text-xs text-white/60 flex items-center gap-1.5"
                            >
                              <Check className="w-3 h-3 text-green-400" />
                              {amenity}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Property Details Modal */}
    <Dialog open={showPropertyDetails} onOpenChange={setShowPropertyDetails}>
      <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light tracking-tight">
            {hotel.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image Carousel */}
          {((hotel.images && hotel.images.length > 0) || hotel.thumbnail) && (
            <div className="relative">
              <div className="overflow-hidden rounded-lg" ref={emblaRef}>
                <div className="flex">
                  {hotel.images && hotel.images.length > 0 ? (
                    hotel.images.map((img, index) => {
                      const imgUrl = typeof img === 'object' ? (img.original_image || img.thumbnail) : img;
                      return (
                        <div key={index} className="flex-[0_0_100%] min-w-0">
                          <div className="aspect-video rounded-lg overflow-hidden">
                            <img
                              src={imgUrl}
                              alt={`${hotel.name} - Image ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23374151" width="200" height="200"/%3E%3C/svg%3E';
                              }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : hotel.thumbnail && (
                    <div className="flex-[0_0_100%] min-w-0">
                      <div className="aspect-video rounded-lg overflow-hidden">
                        <img
                          src={hotel.thumbnail}
                          alt={hotel.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23374151" width="200" height="200"/%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Carousel Navigation Buttons */}
              {hotel.images && hotel.images.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollPrev();
                    }}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollNext();
                    }}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Hotel Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Rating */}
              <div>
                <h3 className="text-sm font-normal text-white/60 mb-2">Rating</h3>
                <div className="flex items-center gap-3">
                  {hotel.hotel_class && (
                    <div className="flex items-center gap-1">
                      {[...Array(parseInt(hotel.hotel_class))].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  )}
                  {rating && (
                    <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                      {rating.label} ({hotel.reviews || 0} reviews)
                    </Badge>
                  )}
                </div>
              </div>

              {/* Location */}
              {hotel.address && (
                <div>
                  <h3 className="text-sm font-normal text-white/60 mb-2">Location</h3>
                  <div className="flex items-start gap-2 text-white">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">
                      {typeof hotel.address === 'string' ? hotel.address : (hotel.address.full || hotel.address.city || hotel.address.country)}
                    </span>
                  </div>
                </div>
              )}

              {/* Check-in/out dates */}
              <div>
                <h3 className="text-sm font-normal text-white/60 mb-2">Stay Dates</h3>
                <div className="text-sm text-white">
                  <div>Check-in: {hotel.check_in_date}</div>
                  <div>Check-out: {hotel.check_out_date}</div>
                  <div className="text-white/60 mt-1">{hotel.nights} night{hotel.nights > 1 ? 's' : ''}</div>
                </div>
              </div>

              {/* Features */}
              {hotel.free_cancellation && (
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                  Free Cancellation
                </Badge>
              )}
              {hotel.eco_certified && (
                <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
                  Eco-Certified
                </Badge>
              )}
            </div>

            {/* Right Column - Pricing */}
            <div className="space-y-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-sm font-normal text-white/60 mb-3">Price Breakdown</h3>
                <div className="space-y-2">
                  {hotel.nightly_rate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">{formatPrice(hotel.nightly_rate)}/night</span>
                      <span className="text-white">{hotel.nights} nights</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="text-white font-medium">Total</span>
                      <span className="text-white text-xl font-semibold">{formatPrice(hotel.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nearby Places */}
              {hotel.nearby_places && hotel.nearby_places.length > 0 && (
                <div>
                  <h3 className="text-sm font-normal text-white/60 mb-2">Nearby</h3>
                  <div className="space-y-1">
                    {hotel.nearby_places.slice(0, 5).map((place, index) => (
                      <div key={index} className="text-sm text-white/70">
                        {place.name || place}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Amenities */}
          {hotel.amenities && hotel.amenities.length > 0 && (
            <div>
              <h3 className="text-sm font-normal text-white/60 mb-3">Amenities</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {hotel.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-white/70">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Room Rates Modal */}
    {showRoomRatesModal && hotelWithRates && (
      <RoomRatesModal
        hotel={hotelWithRates}
        onSelectRate={handleRateSelection}
        onClose={() => setShowRoomRatesModal(false)}
      />
    )}
    </>
  );
}

export default memo(StayOfferCard);
